
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { NETWORK_TYPE } from "~/types/Edge";
import { OrderConfig, orderConfigDefault } from "~/types/orders/vmcConfig";
import { LOCATION_TYPE } from "~/views/order/vmcOrder/VMCController";

type initState = {
    orderConfig: OrderConfig
    isOrderRadio : boolean
    isNetworkTypeManuallyEdited: boolean // Flag to track if user manually edited networkType
    showVmFormFactor5g: boolean
}

const initialState: initState = {
    orderConfig: orderConfigDefault,
    isOrderRadio : true,
    isNetworkTypeManuallyEdited: false,
    showVmFormFactor5g: false
}


const orderCreateSlice = createSlice({
    name: "orderCreate",
    initialState,
    reducers: {
        setOrderConfig(state, action: PayloadAction<OrderConfig>) {
            state.orderConfig = action.payload;
        },
        resetOrderConfig(state) {
            state.orderConfig = orderConfigDefault;
            state.isNetworkTypeManuallyEdited = false;
            state.showVmFormFactor5g = false;
        },

        // VMC Config

        setVMCConfig(state, action: PayloadAction<OrderConfig["vmc"]>) {
            state.orderConfig.vmc = action.payload;
        },

        setResetVMCConfig(state) {
            state.orderConfig.vmc = orderConfigDefault.vmc;
        },

        setTenantId(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.tenantId = action.payload;
        },

        setServiceInstanceName(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.serviceInstanceName = action.payload;
        },

        setServiceInstanceDisplayName(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.serviceInstanceDisplayName = action.payload;
        },

        setRelease(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.release = action.payload;
        },

        setReleaseTag(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.releaseTag = action.payload;
        },

        setServiceInstanceLocationType(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.serviceInstanceLocationType = action.payload;

            if (action.payload === "GCP") {
                delete state.orderConfig.vmc.serviceInstanceLatitude;
                delete state.orderConfig.vmc.serviceInstanceLongitude;
            }

        },

        setServiceInstanceLocation(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.serviceInstanceLocation = action.payload;
        },

        setVMCLocations(state, action: PayloadAction<{
            lat: number;
            lng: number;
        }>) {
            state.orderConfig.vmc.serviceInstanceLatitude = action.payload.lat;
            state.orderConfig.vmc.serviceInstanceLongitude = action.payload.lng;
        },

        setNetworkType(state, action: PayloadAction<NETWORK_TYPE>) {
            state.orderConfig.edge.networkType = action.payload;
        },

        setIsNetworkTypeManuallyEdited(state, action: PayloadAction<boolean>) {
            state.isNetworkTypeManuallyEdited = action.payload;
        },

        setShowVmFormFactor5g(state, action: PayloadAction<boolean>) {
            state.showVmFormFactor5g = action.payload;
        },

        setMcc(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.mcc = action.payload;
        },

        setMnc(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.mnc = action.payload;
        },

        setTac(state, action: PayloadAction<number>) {
            state.orderConfig.vmc.tac = action.payload;
        },

        setDeploymentCloudId(state, action: PayloadAction<string>) {
            state.orderConfig.vmc.deploymentCloudId = action.payload;
        },

        // Edge Config

        setEdgeConfig(state, action: PayloadAction<OrderConfig["edge"]>) {
            state.orderConfig.edge = action.payload;
        },

        setEdgeName (state, action: PayloadAction<string>) {
            state.orderConfig.edge.edgeName = action.payload;
        },

        setEdgeInterfaceCount(state, action: PayloadAction<number>) {
            state.orderConfig.edge.edgeInterfaceCount = action.payload;
        },

        setEdgeLocationType(state, action: PayloadAction<string>) {
            state.orderConfig.edge.edgeLocationType = action.payload;

            // set default based on location type
            if(action.payload === LOCATION_TYPE.AWS) {
                state.orderConfig.edge.edgeLocation = "us-west-2a";
            } else if(action.payload === LOCATION_TYPE.GCP) {
                state.orderConfig.edge.edgeLocation = "us-central1-a";
            } else if(action.payload === LOCATION_TYPE.PRIVATE_CLOUD) {
                // Drop stale cloud zone + coords so map must be re-selected
                state.orderConfig.edge.edgeLocation = "";
                delete state.orderConfig.edge.edgeLatitude;
                delete state.orderConfig.edge.edgeLongitude;
            }
        },

        setEdgeLocation(state, action: PayloadAction<string>) {
            state.orderConfig.edge.edgeLocation = action.payload;
        },

        setEdgeLocations(state, action: PayloadAction<{
            lat: number;
            lng: number;
        }>) {
            state.orderConfig.edge.edgeLatitude = action.payload.lat;
            state.orderConfig.edge.edgeLongitude = action.payload.lng;
        },

        setEdgeIpsecMode(state, action: PayloadAction<boolean>) {
            state.orderConfig.edge.edgeIpsecMode = action.payload;
        },

        setNetworkMode(state, action: PayloadAction<string>) {
            state.orderConfig.edge.networkMode = action.payload;
            if(action.payload === "NETWORK_EXTENSION") {
                state.orderConfig.edge.edgeInterfaceCount = 2;
                state.orderConfig.edge.edgeLocationType = LOCATION_TYPE.PRIVATE_CLOUD;
                state.orderConfig.edge.edgeLocation = "";
                delete state.orderConfig.edge.edgeLatitude;
                delete state.orderConfig.edge.edgeLongitude;
            }
        },

        setEdgeRelease(state, action: PayloadAction<string>) {
            state.orderConfig.edge.edgeRelease = action.payload;
        },

        setEdgeReleaseTag(state, action: PayloadAction<string>) {
            state.orderConfig.edge.edgeReleaseTag = action.payload;
        },

        setEdgeFormFactor(state, action: PayloadAction<"VM" | "BAREMETAL">) {
            state.orderConfig.edge.formFactor = action.payload;
        },

        setEdgeServices(state, action: PayloadAction<string>) {
            state.orderConfig.edge.services = action.payload;

            if(action.payload === "CARRIER_GATEWAY") {
                state.orderConfig.edge.networkMode = "GATEWAY";
                state.orderConfig.edge.edgeInterfaceCount = 2;
            }
        },

        setServices(state, action: PayloadAction<string>) {
            state.orderConfig.edge.services = action.payload;
        },

        // Radio Config

        setIsOrderRadio(state, action: PayloadAction<boolean>) {
            state.isOrderRadio = action.payload;
        },

        resetRadioConfig(state) {
            state.orderConfig.radio = orderConfigDefault.radio;
        },

        setRadioConfig(state, action: PayloadAction<OrderConfig["radio"]>) {
            state.orderConfig.radio = action.payload;
        },

        setRadioName(state, action: PayloadAction<string>) {
            state.orderConfig.radio.RadioName = action.payload;
        },

        setRadioAltitude(state, action: PayloadAction<number>) {
            state.orderConfig.radio.RadioAltitude = action.payload;
        },

        setRadioLocations(state, action: PayloadAction<{
            lat: number;
            lng: number;
        }>) {
            state.orderConfig.radio.RadioLatitude = action.payload.lat;
            state.orderConfig.radio.RadioLongitude = action.payload.lng;
        },

        setRadioLocationName(state, action: PayloadAction<string>) {
            state.orderConfig.radio.RadioLocation = action.payload;
        },

        setRadioLocationSyncMode(state, action: PayloadAction<string>) {
            state.orderConfig.radio.RadioLocationSyncMode = action.payload;
        },


        // Device Config

        setDeviceConfig(state, action: PayloadAction<OrderConfig["device"]>) {
            state.orderConfig.device = action.payload;
        },

        setSubscriberCount(state, action: PayloadAction<number>) {
            state.orderConfig.device.subscriberCount = action.payload;
        },

        setESIMCount(state, action: PayloadAction<number>) {
            state.orderConfig.device.esimCount = action.payload;
        },

        setIMSIMgmtType(state, action: PayloadAction<string>) {
            state.orderConfig.device.imsiMgmtType = action.payload;

            if(action.payload === "CUSTOM") {
                delete state.orderConfig.device.psimAllocationType;
                delete state.orderConfig.device.psimIccidNumberList;
                delete state.orderConfig.device.psimBlockId;
                delete state.orderConfig.device.esimBlockId;
            }
        },

        setPSIMAllocationType(state, action: PayloadAction<string>) {
            state.orderConfig.device.psimAllocationType = action.payload;
            if(action.payload === "ICCID") {
                delete state.orderConfig.device.psimBlockId;
            } else if(action.payload === "BLOCK") {
                delete state.orderConfig.device.psimIccidNumberList;
            }
        },

        setPSIMICCIDNumberList(state, action: PayloadAction<string[]>) {
            state.orderConfig.device.psimIccidNumberList = action.payload;
        },

        setPSIMBlockId(state, action: PayloadAction<string>) {
            state.orderConfig.device.psimBlockId = action.payload;
        },

        setESIMBlockId(state, action: PayloadAction<string>) {
            state.orderConfig.device.esimBlockId = action.payload;
        },

    },
});


export const orderCreateActions = orderCreateSlice.actions;

export default orderCreateSlice.reducer;